// Payments — the daily flow's CHECKPOINT between selling and dispatching.
// Every won deal lands here with its reported payment; the admin checks the
// data, corrects it if needed, and APPROVES — only then does the order become
// ready to dispatch. Also collects open advance balances.
// Shared by the ops-admin workspace and (potentially) the superadmin panel
// via the api prop (same factory as the dispatch board).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiEdit3,
  FiRefreshCw,
  FiSearch,
  FiUser,
} from 'react-icons/fi';
import { Alert, Button } from '../common/index.js';
import { PaymentModal, BalanceModal } from './DispatchBoard.jsx';
import { useOpsDay } from '../../hooks/useOpsDay';
import { PAYMENT_MODES, isKnownMode, modeLabel } from '../../constants/paymentModes';
import {
  blocksOnCollectedBalance, buildCorrectionBody, initialForm, whatChanged,
} from './counterPaymentForm';

const formatPrice = (value) => {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const shortDate = (value) => String(value || '').split(' at ')[0];

const TABS = [
  { value: 'to_confirm', label: 'To approve' },
  { value: 'pending', label: 'No payment yet' },
  { value: 'balance_due', label: 'Balance due' },
  { value: 'recorded', label: 'Approved' },
  { value: 'counter', label: 'Counter sales' },
];

// How much of the counter book the Payments screen reads. The book grows
// forever; the approval queue and open balances are never capped.
const BOOK_LIMIT = 100;

// One counter sale on the payments list — personal/walk-in money. Goes
// through the same checkpoint as inquiry payments: the admin checks the
// entry and APPROVES it; open advance balances get collected here too.
export const CounterSaleCard = ({ sale, busy, onApprove, onEditPrice, onCollectBalance }) => {
  const car = [sale.car_brand, sale.car_model, sale.car_year].filter(Boolean).join(' ');
  const isAdvance = sale.payment_type === 'advance';
  const balanceOpen = isAdvance && !sale.balance_paid && Number(sale.balance_due) > 0;
  const typeMeta = sale.sale_type === 'walkin'
    ? { label: 'Walk-in', cls: 'border-sky-200 bg-sky-50 text-sky-700' }
    : { label: 'Personal', cls: 'border-violet-200 bg-violet-50 text-violet-700' };
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${balanceOpen ? 'border-rose-200' : sale.payment_confirmed ? 'border-slate-200' : 'border-sky-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-bold text-slate-900">{sale.customer?.name || 'Customer'}</h3>
          <p className="mt-0.5 text-sm text-slate-500">{sale.customer?.phone_number}{car ? ` · ${car}` : ''}</p>
          {sale.items?.length > 0 && (
            <p className="mt-1 truncate text-xs text-slate-500">
              {sale.items.map((it) => `${it.name}${it.quantity > 1 ? ` ×${it.quantity}` : ''}`).join(', ')}
            </p>
          )}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${typeMeta.cls}`}>
              {typeMeta.label}
            </span>
            {sale.payment_confirmed ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                <FiCheckCircle className="h-3 w-3" /> Approved
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-bold text-sky-700">
                <FiClock className="h-3 w-3" /> To approve
              </span>
            )}
            {isAdvance ? (
              sale.balance_paid ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                  <FiCheckCircle className="h-3 w-3" /> Advance · balance collected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                  <FiCreditCard className="h-3 w-3" /> Advance {formatPrice(sale.advance_amount)}
                </span>
              )
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                <FiCheckCircle className="h-3 w-3" /> Paid in full
              </span>
            )}
            {balanceOpen && (
              <span className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">
                Balance {formatPrice(sale.balance_due)} due
              </span>
            )}
            {sale.payment_mode && (
              <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                {sale.payment_mode_label || modeLabel(sale.payment_mode)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="text-sm font-bold text-slate-800">{formatPrice(sale.total_amount) || '—'}</span>
          <span className="text-[11px] text-slate-400">{String(sale.sold_at || '').split('-').reverse().join('-')} · by {sale.entered_by_name || '-'}</span>
          {!sale.payment_confirmed && (
            <div className="flex flex-wrap justify-end gap-1.5">
              <Button size="sm" variant="success" loading={busy} onClick={() => onApprove(sale)} className="gap-1.5">
                <FiCheckCircle className="h-3.5 w-3.5" /> Approve payment
              </Button>
              <Button size="sm" variant="secondary" onClick={() => onEditPrice(sale)} className="gap-1.5">
                <FiEdit3 className="h-3.5 w-3.5" /> Correct price
              </Button>
            </div>
          )}
          {balanceOpen && (
            <Button size="sm" variant="primary" onClick={() => onCollectBalance(sale)} className="gap-1.5">
              <FiCreditCard className="h-3.5 w-3.5" /> Balance received
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Fix the money at the checkpoint — what was taken, HOW it was paid, and on
// what terms. It used to correct the price alone, so a sale entered as cash
// that was really GPay, or typed as an advance when the customer paid in
// full, could not be put right from here at all: the only mode picker in the
// panel lives on the ops Counter Sales screen, which the superadmin has no
// route to. Every one of these fields is money — a mode moves it between the
// drawer and the bank, a term moves it between days — so any change to them
// asks for a reason and lands in the superadmin's record.
export const CounterPriceModal = ({ sale, submitting, error, onClose, onSave }) => {
  const entered = Number(sale.total_amount) || 0;
  const start = initialForm(sale);
  const [form, setForm] = useState(start);
  const [localError, setLocalError] = useState('');
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const { total, ptype, advance, mode, reason } = form;
  const totalNum = Number(total);
  const isAdvance = ptype === 'advance';         // driven by the toggle, not by stored state
  const changed = whatChanged(sale, form);
  const priceChanged = changed.price && Number.isFinite(totalNum) && totalNum > 0;
  const blockedByCollection = blocksOnCollectedBalance(sale, form);

  const submit = () => {
    const { body, error } = buildCorrectionBody(sale, form);
    setLocalError(error || '');
    if (body) onSave(body);
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-4 text-sm font-bold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={submitting ? undefined : onClose}>
      <div className="flex max-h-[92vh] w-full max-w-sm flex-col overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-900">Correct this payment</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          {sale.customer?.name}{sale.customer?.phone_number ? ` · ${sale.customer.phone_number}` : ''}
        </p>
        {(error || localError) && <div className="mt-3"><Alert type="error" message={error || localError} /></div>}

        <label className="mt-4 mb-1.5 block text-sm font-semibold text-slate-700">Total actually taken *</label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
          <input
            type="number"
            min="0"
            step="any"
            inputMode="numeric"
            value={total}
            onChange={(e) => set('total')(e.target.value)}
            className={inputCls}
          />
        </div>
        {priceChanged && entered > 0 && (
          <p className="mt-2 text-xs font-bold text-violet-600">
            {totalNum > entered ? '+' : '−'}{formatPrice(Math.abs(totalNum - entered))} vs the {formatPrice(entered)} entered
          </p>
        )}

        <p className="mt-4 mb-1.5 text-sm font-semibold text-slate-700">Paid in full, or an advance?</p>
        <div className="grid grid-cols-2 gap-2">
          {[{ v: 'full', l: 'Full payment' }, { v: 'advance', l: 'Advance' }].map((t) => (
            <button
              key={t.v}
              type="button"
              onClick={() => set('ptype')(t.v)}
              className={`rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition-all ${ptype === t.v ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {isAdvance && (
          <>
            <label className="mt-3 mb-1.5 block text-sm font-semibold text-slate-700">Advance collected *</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input
                type="number"
                min="0"
                step="any"
                inputMode="numeric"
                value={advance}
                onChange={(e) => set('advance')(e.target.value)}
                className={inputCls}
              />
            </div>
            {Number(advance) > 0 && Number(total) > Number(advance) && (
              <p className="mt-1.5 text-xs font-semibold text-amber-600">
                Balance {formatPrice(Number(total) - Number(advance))} to collect later
              </p>
            )}
          </>
        )}

        <p className="mt-4 mb-1.5 text-sm font-semibold text-slate-700">
          Paid by {!mode && <span className="font-normal text-slate-400">— not recorded</span>}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {PAYMENT_MODES.map((m) => (
            <button
              key={m.v}
              type="button"
              onClick={() => set('mode')(m.v)}
              className={`rounded-lg border px-2.5 py-1.5 text-xs font-bold transition-all ${mode === m.v ? 'border-violet-500 bg-violet-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
            >
              {m.l}
            </button>
          ))}
        </div>
        {sale.payment_mode && !isKnownMode(sale.payment_mode) && (
          <p className="mt-1.5 text-xs font-semibold text-amber-600">
            Recorded as "{sale.payment_mode}" — not a payment mode we use. Pick the right one.
          </p>
        )}

        {/* Money already collected is an event, not a setting. Switching such
            a sale to a full payment would erase it from the day it came in, so
            the collection has to be reopened first — say that here, in place. */}
        {blockedByCollection && (
          <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
            This sale records a balance collected
            {sale.balance_paid_at ? ` on ${shortDate(sale.balance_paid_at)}` : ''}
            {sale.balance_paid_mode_label ? ` by ${sale.balance_paid_mode_label}` : ''}.
            Reopen that collection first if it never happened — changing the terms here
            would take that money out of the day's book without a trace.
          </p>
        )}

        {/* The superadmin never sees the correction happen — only this note */}
        {changed.any && (
          <>
            <label className="mt-3 mb-1.5 block text-sm font-semibold text-slate-700">
              What are you correcting? *
            </label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => set('reason')(e.target.value)}
              placeholder="e.g. paid by GPay, not cash — customer showed the receipt"
              className="w-full resize-none rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
            <p className="mt-1 text-xs text-slate-400">Goes to the superadmin's price-change record.</p>
          </>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" loading={submitting} onClick={submit} className="gap-2">
            <FiEdit3 className="h-4 w-4" /> Save payment
          </Button>
        </div>
      </div>
    </div>
  );
};

// One reported payment, in words the admin can check at a glance.
const reportedText = (p) => {
  if (!p) return '';
  if (p.type === 'advance') {
    return `Advance ${formatPrice(p.advance_amount)} of ${formatPrice(p.total)}${p.mode_label ? ` · ${p.mode_label}` : ''}`;
  }
  if (p.type === 'final') {
    return `Final rate ${formatPrice(p.final_amount)}${p.mode_label ? ` · ${p.mode_label}` : ''}`;
  }
  return `Full ${formatPrice(p.total)}${p.mode_label ? ` · ${p.mode_label}` : ''}`;
};

const PaymentCard = ({ row, busy, onApprove, onEdit, onCollectBalance }) => {
  const p = row.payment;
  const reported = Boolean(p) && !p.confirmed;
  const confirmed = Boolean(p) && p.confirmed;
  const balanceOpen = Boolean(
    p && p.type === 'advance' && !p.balance_paid
    && p.balance_due && Number(p.balance_due) > 0
  );
  const parts = (row.parts || []).filter((x) => x.sellable !== false);

  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${reported ? 'border-sky-200' : confirmed ? 'border-slate-200' : 'border-amber-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${confirmed ? 'bg-emerald-50 text-emerald-600' : reported ? 'bg-sky-50 text-sky-600' : 'bg-amber-50 text-amber-600'}`}>
            {confirmed ? <FiCheckCircle className="h-5 w-5" /> : reported ? <FiCreditCard className="h-5 w-5" /> : <FiClock className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-slate-900">{row.customer_name || 'Customer'}</h3>
            <p className="mt-0.5 text-sm text-slate-500">{row.car}</p>
            <p className="mt-0.5 text-xs font-medium text-slate-400">
              {formatPrice(p?.total ?? row.total) || '—'} · by {row.salesperson_name || '-'} · won {shortDate(row.won_at)}
            </p>
            {parts.length > 0 && (
              <p className="mt-1 truncate text-xs text-slate-500">
                {parts.map((x) => `${x.name}${x.quantity > 1 ? ` ×${x.quantity}` : ''}`).join(', ')}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2">
          {confirmed ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
              <FiCheckCircle className="h-3.5 w-3.5" /> Approved {shortDate(p.confirmed_at)}
            </span>
          ) : reported ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700">
              <FiCreditCard className="h-3.5 w-3.5" /> Reported by {p.reported_by === 'sales' ? 'sales' : p.reported_by}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
              <FiAlertCircle className="h-3.5 w-3.5" /> No payment reported
            </span>
          )}
          {balanceOpen && (
            <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600">
              <FiCreditCard className="h-3.5 w-3.5" /> Balance {formatPrice(p.balance_due)} due
            </span>
          )}
        </div>
      </div>

      {/* What was reported — the data the admin is checking */}
      {p && (
        <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2.5 ${confirmed ? 'border-slate-100 bg-slate-50' : 'border-sky-100 bg-sky-50/60'}`}>
          <p className="text-sm font-semibold text-slate-700">{reportedText(p)}</p>
          {p.balance_paid && p.type === 'advance' && (
            <span className="text-xs font-semibold text-emerald-600">
              Balance collected{p.balance_paid_mode_label ? ` · ${p.balance_paid_mode_label}` : ''}
            </span>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap justify-end gap-1.5">
        {reported && (
          <>
            <Button size="sm" variant="success" loading={busy} onClick={() => onApprove(row)} className="gap-1.5">
              <FiCheckCircle className="h-3.5 w-3.5" /> Approve payment
            </Button>
            <Button size="sm" variant="secondary" onClick={() => onEdit(row)} className="gap-1.5">
              <FiEdit3 className="h-3.5 w-3.5" /> Correct
            </Button>
          </>
        )}
        {!p && (
          <Button size="sm" variant="primary" onClick={() => onEdit(row)} className="gap-1.5">
            <FiCreditCard className="h-3.5 w-3.5" /> Record payment
          </Button>
        )}
        {confirmed && balanceOpen && (
          <Button size="sm" variant="primary" onClick={() => onCollectBalance(row)} className="gap-1.5">
            <FiCreditCard className="h-3.5 w-3.5" /> Balance received
          </Button>
        )}
      </div>
    </div>
  );
};

const PaymentsBoard = ({ api, counterApi }) => {
  const { refresh: refreshOpsDay } = useOpsDay();
  const [tab, setTab] = useState('to_confirm');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  // Counter sales ride the same checkpoint: unapproved entries (any day),
  // open advance balances (any day), and today's book for the other tabs.
  const [counterPending, setCounterPending] = useState(null);
  const [counterOpen, setCounterOpen] = useState(null);
  const [counterBook, setCounterBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editTarget, setEditTarget] = useState(null);
  const [balanceTarget, setBalanceTarget] = useState(null);
  const [counterBalanceTarget, setCounterBalanceTarget] = useState(null);
  const [counterPriceTarget, setCounterPriceTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [approveBusyId, setApproveBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qp = q || undefined;
      // The counter book is NOT date-locked: the entry team types the night
      // book and the admin checks it the next morning, so pinning these to
      // "today" hid yesterday's sales. Same window the Counter Sales page uses.
      const [inq, pending, open, book] = await Promise.all([
        // Inquiry-rail payments for the current tab
        tab === 'counter' ? null : api.payments({ status: tab, q }),
        // Counter entries still to approve — ANY day, never out of sight
        counterApi.list({ confirmed: 0, q: qp }),
        // Open advance balances — ANY day, money owed never ages out
        counterApi.list({ open_balance: 1, q: qp }),
        // Recent book — approved slice for "Approved", everything for "Counter sales"
        tab === 'recorded' ? counterApi.list({ confirmed: 1, limit: BOOK_LIMIT, q: qp })
          : tab === 'counter' ? counterApi.list({ limit: BOOK_LIMIT, q: qp })
            : null,
      ]);
      if (inq) setData(inq.data ?? inq);
      setCounterPending((pending.data ?? pending).sales || []);
      setCounterOpen((open.data ?? open).sales || []);
      setCounterBook(book ? ((book.data ?? book).sales || []) : null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load payments');
    } finally {
      setLoading(false);
    }
    // Keep the nav badge in step with what this board just showed/cleared
    refreshOpsDay();
  }, [api, counterApi, tab, q, refreshOpsDay]);

  useEffect(() => { load(); }, [load]);

  // Approve EXACTLY what was reported — one tap, no retyping.
  const approveAsReported = async (row) => {
    const p = row.payment;
    if (!p) return;
    setApproveBusyId(row.inquiry_id);
    setError('');
    try {
      await api.setPayment(row.inquiry_id, {
        payment_mode: p.mode,
        payment_type: p.type,
        advance_amount: p.type === 'advance' ? p.advance_amount : undefined,
        final_amount: p.type === 'final' ? p.final_amount : undefined,
        // A reported final rate already differs from the quote — the admin is
        // accepting it, not setting it, so the log records who to ask.
        reason: p.type === 'final'
          ? `Approved as reported by ${p.reported_by === 'sales' ? 'the salesperson' : p.reported_by || 'sales'}`
          : undefined,
      });
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Approve failed');
    } finally {
      setApproveBusyId(null);
    }
  };

  // Approve a counter sale's entry — one tap, same idea as the inquiry rail.
  const approveCounter = async (sale) => {
    setApproveBusyId(`counter-${sale.id}`);
    setError('');
    try {
      await counterApi.confirmPayment(sale.id, true);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Approve failed');
    } finally {
      setApproveBusyId(null);
    }
  };

  // Save the corrected price of a counter sale (total, + advance if any).
  const saveCounterPrice = async (body) => {
    setSubmitting(true);
    setModalError('');
    try {
      await counterApi.update(counterPriceTarget.id, body);
      setCounterPriceTarget(null);
      load();
    } catch (e) {
      const d = e?.response?.data;
      setModalError(d?.error || d?.total_amount || d?.advance_amount || e.message || 'Could not save the price');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPayment = async (payload) => {
    setSubmitting(true);
    setModalError('');
    try {
      await api.setPayment(editTarget.inquiry_id, payload);
      setEditTarget(null);
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not record the payment');
    } finally {
      setSubmitting(false);
    }
  };

  const collectBalance = async (mode) => {
    setSubmitting(true);
    setModalError('');
    try {
      if (counterBalanceTarget) {
        await counterApi.saleBalance(counterBalanceTarget.id, { paid: true, mode });
        setCounterBalanceTarget(null);
      } else {
        await api.collectBalance(balanceTarget.inquiry_id, { paid: true, mode });
        setBalanceTarget(null);
      }
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not record the balance');
    } finally {
      setSubmitting(false);
    }
  };

  // Which counter cards belong on the CURRENT tab — they sit in the same
  // list as the inquiry payments, so nothing hides behind a separate tab.
  const counterRows = useMemo(() => {
    if (tab === 'to_confirm') return counterPending || [];
    if (tab === 'balance_due') return counterOpen || [];
    if (tab === 'recorded') return counterBook || [];
    if (tab === 'counter') {
      // The book + every still-open advance (an old advance may fall outside
      // the recent slice, and money owed must never drop out of sight)
      const merged = [...(counterBook || [])];
      const seen = new Set(merged.map((s) => s.id));
      (counterOpen || []).forEach((s) => { if (!seen.has(s.id)) merged.push(s); });
      return merged;
    }
    return [];
  }, [tab, counterPending, counterOpen, counterBook]);

  const counterDue = useMemo(
    () => (counterOpen || []).reduce((sum, s) => sum + (Number(s.balance_due) || 0), 0),
    [counterOpen],
  );
  const totalDue = Number(data?.total_balance_due || 0) + counterDue;
  const dueCount = (data?.balance_due_count || 0) + (counterOpen?.length || 0);

  const counts = useMemo(() => ({
    to_confirm: (data?.to_confirm_count ?? 0) + (counterPending?.length ?? 0),
    pending: data?.pending_count ?? 0,
    balance_due: (data?.balance_due_count ?? 0) + (counterOpen?.length ?? 0),
    recorded: null,   // no server count — the tab shows without a badge
    counter: tab === 'counter' ? counterRows.length : null,
  }), [data, counterPending, counterOpen, tab, counterRows]);
  const rows = data?.payments ?? [];

  return (
    <div className="space-y-4">
      {error && <Alert type="error" message={error} />}

      {/* Money still to come in (sales + counter) — independent of the tab */}
      {totalDue > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5 text-sm">
          <span className="inline-flex items-center gap-1.5 font-bold text-rose-700">
            <FiCreditCard className="h-4 w-4" /> {formatPrice(totalDue)} still to collect
          </span>
          <span className="text-xs font-medium text-rose-600/80">
            open balances on {dueCount} advance deal{dueCount === 1 ? '' : 's'}
          </span>
        </div>
      )}

      {/* Tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => {
            const active = tab === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
              >
                {t.label}
                {counts[t.value] != null && (
                  <span className={`rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                    {counts[t.value]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Customer, car or part…"
              className="w-64 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 disabled:opacity-50"
            aria-label="Refresh"
            title="Refresh"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* List — counter cards and inquiry payments share the same tab */}
      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : counterRows.length === 0 && (tab === 'counter' || rows.length === 0) ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
          <FiUser className="mx-auto mb-2 h-8 w-8 text-slate-200" />
          <p className="text-sm text-slate-400">
            {tab === 'to_confirm' ? 'Nothing waiting for approval'
              : tab === 'pending' ? 'Every won deal has a payment reported'
                : tab === 'balance_due' ? 'No open balances'
                  : tab === 'counter' ? 'No counter sales in the book yet'
                    : 'Nothing approved yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {counterRows.map((sale) => (
            <CounterSaleCard
              key={`counter-${sale.id}`}
              sale={sale}
              busy={approveBusyId === `counter-${sale.id}`}
              onApprove={approveCounter}
              onEditPrice={(s) => { setModalError(''); setCounterPriceTarget(s); }}
              onCollectBalance={(s) => { setModalError(''); setCounterBalanceTarget(s); }}
            />
          ))}
          {tab !== 'counter' && rows.map((row) => (
            <PaymentCard
              key={row.inquiry_id}
              row={row}
              busy={approveBusyId === row.inquiry_id}
              onApprove={approveAsReported}
              onEdit={(r) => { setModalError(''); setEditTarget(r); }}
              onCollectBalance={(r) => { setModalError(''); setBalanceTarget(r); }}
            />
          ))}
        </div>
      )}

      {editTarget && (
        <PaymentModal
          order={editTarget}
          submitting={submitting}
          error={modalError}
          onClose={() => { setEditTarget(null); setModalError(''); }}
          onConfirm={confirmPayment}
        />
      )}
      {balanceTarget && (
        <BalanceModal
          order={balanceTarget}
          submitting={submitting}
          error={modalError}
          onClose={() => { setBalanceTarget(null); setModalError(''); }}
          onConfirm={collectBalance}
        />
      )}
      {counterPriceTarget && (
        <CounterPriceModal
          sale={counterPriceTarget}
          submitting={submitting}
          error={modalError}
          onClose={() => { setCounterPriceTarget(null); setModalError(''); }}
          onSave={saveCounterPrice}
        />
      )}
      {counterBalanceTarget && (
        <BalanceModal
          order={{
            customer_name: counterBalanceTarget.customer?.name,
            car: [counterBalanceTarget.car_brand, counterBalanceTarget.car_model,
              counterBalanceTarget.car_year].filter(Boolean).join(' '),
            payment: { balance_due: counterBalanceTarget.balance_due },
          }}
          submitting={submitting}
          error={modalError}
          onClose={() => { setCounterBalanceTarget(null); setModalError(''); }}
          onConfirm={collectBalance}
        />
      )}
    </div>
  );
};

export default PaymentsBoard;
